import { useCallback, useEffect, useState } from 'react';
import client, { mensajeDeError } from '../../api/client';
import Button from '../../components/ui/Button';
import Badge from '../../components/ui/Badge';
import Modal from '../../components/ui/Modal';
import Spinner from '../../components/ui/Spinner';
import { Campo, Etiqueta, Entrada, Selector, AreaTexto, MensajeError } from '../../components/ui/Form';
import { TituloSeccion, BarraAcciones, TablaEnvoltura, Tabla, CeldaAcciones, SinDatos } from './comunes';

const vacio = {
  nombre: '',
  descripcion: '',
  tipo: 'por_clases',
  numeroClases: 8,
  precio: '',
  diasVigencia: 30,
  activo: true,
};

function PaquetesAdmin() {
  const [paquetes, setPaquetes] = useState(null);
  const [editando, setEditando] = useState(null);
  const [error, setError] = useState('');
  const [guardando, setGuardando] = useState(false);

  const cargar = useCallback(() => {
    client
      .get('/paquetes', { params: { todos: '1' } })
      .then(({ data }) => setPaquetes(data))
      .catch(() => setPaquetes([]));
  }, []);

  useEffect(cargar, [cargar]);

  const abrir = (p) => {
    setError('');
    setEditando(
      p
        ? {
            id: p.id,
            nombre: p.nombre,
            descripcion: p.descripcion || '',
            tipo: p.tipo,
            numeroClases: p.numero_clases || 8,
            precio: p.precio,
            diasVigencia: p.dias_vigencia,
            activo: !!p.activo,
          }
        : { ...vacio }
    );
  };

  const guardar = async (e) => {
    e.preventDefault();
    setGuardando(true);
    setError('');
    try {
      if (editando.id) {
        await client.put(`/paquetes/${editando.id}`, editando);
      } else {
        await client.post('/paquetes', editando);
      }
      setEditando(null);
      cargar();
    } catch (err) {
      setError(mensajeDeError(err));
    } finally {
      setGuardando(false);
    }
  };

  const desactivar = async (id) => {
    setError('');
    try {
      await client.delete(`/paquetes/${id}`);
      cargar();
    } catch (err) {
      setError(mensajeDeError(err));
    }
  };

  if (!paquetes) return <Spinner />;

  const cambiar = (campo) => (e) => setEditando((d) => ({ ...d, [campo]: e.target.value }));

  return (
    <>
      <TituloSeccion>Paquetes y precios</TituloSeccion>
      <BarraAcciones>
        <Button onClick={() => abrir(null)}>Nuevo paquete</Button>
      </BarraAcciones>
      {error && !editando && <MensajeError role="alert">{error}</MensajeError>}
      <TablaEnvoltura>
        {paquetes.length === 0 ? (
          <SinDatos>Sin paquetes.</SinDatos>
        ) : (
          <Tabla>
            <thead>
              <tr>
                <th>Nombre</th>
                <th>Tipo</th>
                <th>Precio</th>
                <th>Vigencia</th>
                <th>Estado</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {paquetes.map((p) => (
                <tr key={p.id}>
                  <td>{p.nombre}</td>
                  <td>{p.tipo === 'ilimitado' ? 'Ilimitado' : `${p.numero_clases} clases`}</td>
                  <td>${Number(p.precio).toLocaleString('es-MX')}</td>
                  <td>{p.dias_vigencia} días</td>
                  <td>
                    <Badge $tono={p.activo ? 'exito' : 'arena'}>{p.activo ? 'Activo' : 'Inactivo'}</Badge>
                  </td>
                  <CeldaAcciones>
                    <Button $variante="secundario" $chico onClick={() => abrir(p)}>
                      Editar
                    </Button>
                    {!!p.activo && (
                      <Button $variante="peligro" $chico onClick={() => desactivar(p.id)}>
                        Desactivar
                      </Button>
                    )}
                  </CeldaAcciones>
                </tr>
              ))}
            </tbody>
          </Tabla>
        )}
      </TablaEnvoltura>

      <Modal
        abierto={!!editando}
        titulo={editando?.id ? 'Editar paquete' : 'Nuevo paquete'}
        onCerrar={() => setEditando(null)}
      >
        {editando && (
          <form onSubmit={guardar}>
            {error && <MensajeError role="alert">{error}</MensajeError>}
            <Campo>
              <Etiqueta htmlFor="p-nombre">Nombre</Etiqueta>
              <Entrada id="p-nombre" value={editando.nombre} onChange={cambiar('nombre')} required />
            </Campo>
            <Campo>
              <Etiqueta htmlFor="p-desc">Descripción</Etiqueta>
              <AreaTexto id="p-desc" value={editando.descripcion} onChange={cambiar('descripcion')} />
            </Campo>
            <Campo>
              <Etiqueta htmlFor="p-tipo">Tipo</Etiqueta>
              <Selector id="p-tipo" value={editando.tipo} onChange={cambiar('tipo')}>
                <option value="por_clases">Por número de clases</option>
                <option value="ilimitado">Membresía ilimitada</option>
              </Selector>
            </Campo>
            {editando.tipo === 'por_clases' && (
              <Campo>
                <Etiqueta htmlFor="p-num">Número de clases</Etiqueta>
                <Entrada
                  id="p-num"
                  type="number"
                  min="1"
                  value={editando.numeroClases}
                  onChange={cambiar('numeroClases')}
                  required
                />
              </Campo>
            )}
            <Campo>
              <Etiqueta htmlFor="p-precio">Precio (MXN)</Etiqueta>
              <Entrada
                id="p-precio"
                type="number"
                min="0"
                step="0.01"
                value={editando.precio}
                onChange={cambiar('precio')}
                required
              />
            </Campo>
            <Campo>
              <Etiqueta htmlFor="p-dias">Días de vigencia</Etiqueta>
              <Entrada
                id="p-dias"
                type="number"
                min="1"
                value={editando.diasVigencia}
                onChange={cambiar('diasVigencia')}
                required
              />
            </Campo>
            {editando.id && (
              <Campo>
                <Etiqueta>
                  <input
                    type="checkbox"
                    checked={editando.activo}
                    onChange={(e) => setEditando((d) => ({ ...d, activo: e.target.checked }))}
                    style={{ marginRight: '0.5rem' }}
                  />
                  Activo (visible para clientes)
                </Etiqueta>
              </Campo>
            )}
            <Button type="submit" $completo disabled={guardando}>
              {guardando ? 'Guardando…' : 'Guardar'}
            </Button>
          </form>
        )}
      </Modal>
    </>
  );
}

export default PaquetesAdmin;
